import { DATE_TIME_FORMATS, STANDARD_DATE_FORMAT } from "@miplatform/constants";
import { getCurrencyString } from "../currency/currency";
import { formatDateIfValid, getFormattedDate } from "../date/date";
import { groupBy } from "../groupBy/groupBy";

export const pluralize = (count, noun, suffix = "s") =>
  `${count} ${noun}${count !== 1 ? suffix : ""}`;

export const getValueFormat = (val, fieldType, format) => {
  if (val === null || val === undefined || val === "") {
    // prevent falsy 0 values from returning the default placeholder
    return "--";
  }
  if (fieldType === "Date") {
    return getFormattedDate(format || STANDARD_DATE_FORMAT, val);
  }
  if (fieldType === "Currency") {
    return getCurrencyString(val);
  }

  return val;
};

export const sectionMap = (array, field) => {
  const newArr = groupBy(array, (item) => getNestedValue(item, field));
  const sections = Object.entries(newArr).map(([title, data]) => ({
    title,
    data,
  }));

  return sections;
};

export const getUniqueObjects = (arr, field = "id") => {
  const seenIds = new Set();
  return arr.filter((item) => {
    if (!seenIds.has(item[field])) {
      seenIds.add(item[field]);
      return true;
    }
    return false;
  });
};

export const flattenAttempt = (attempt) => ({
  ...flattenHome(attempt?.home),
  ...flattenAddress(attempt?.address || attempt?.home?.address),
  ...attempt,
  id: attempt.id,
  homeId: attempt?.homeId || attempt?.home?.id,
  communityId: attempt?.home?.communityId,
});

export const flattenHome = (home) => ({
  ...home,
  ...flattenAddress(home?.address),
});

export const flattenAddress = (address) => ({
  ...address,
  addressLine1: address?.addressLine1,
  addressLine2: address?.addressLine2,
  city: address?.city,
  regionCode: address?.regionCode,
  postalCode: address?.postalCode,
  latitude: address?.location?.latitude,
  longitude: address?.location?.longitude,
});

export const groupContactsRelationships = (contacts = []) =>
  contacts.reduce((acc, contact) => {
    if (!acc[contact.id]) {
      acc[contact.id] = {
        ...contact,
        relationships: [],
      };
    }

    acc[contact.id].relationships.push({
      id: `${contact.id}${contact.relatedReferenceId}${contact.label}`,
      relatedReferenceId: contact.relatedReferenceId,
      level: contact.level,
      label: contact.label,
      isPrimary: contact.isPrimary,
      modifiedOn: contact.modifiedOn,
    });

    return acc;
  }, {});

/**
 * Helper function to get nested property values by key
 * @param {Object} obj - The object to traverse
 * @param {string} key - The key, supporting dot notation (e.g., "home.name")
 * @returns {*} - The value of the nested property, or undefined if not found
 */
export const getNestedValue = (obj, key) =>
  key.split(".").reduce((acc, part) => acc && acc[part], obj);

/**
 * Parses and formats a field value for display.
 *
 * - If the value is falsy, returns the default value.
 * - If the value is a boolean, returns "Yes" or "No".
 * - If the value is an array, recursively parses each item and joins them with commas.
 * - If the value is a valid date, formats it using the specified format.
 * - Otherwise, returns the original value.
 *
 * @param {*} value - The value to parse.
 * @param {string} [defaultValue="--"] - The fallback value if the input is falsy.
 * @param {string} [dateFormat=DATE_TIME_FORMATS.FORMAT_MM_DD_YY] - The format string to use when formatting dates.
 *
 * @returns {string} A human-readable string representation of the input value.
 */
export const parseFieldValue = (
  value,
  defaultValue = "--",
  dateFormat = DATE_TIME_FORMATS.FORMAT_MM_DD_YY,
  parseOptions = { isCurrency: false, currency: "USD" }
) => {
  if (value === null || value === undefined || value === "") {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.length > 0
      ? value.map(
          (v, i) =>
            `${parseFieldValue(v, defaultValue, dateFormat, parseOptions)}${
              i !== value.length - 1 ? ", " : ""
            }`
        )
      : defaultValue;
  }

  if (typeof value === "number") {
    if (parseOptions.isCurrency) {
      const formattedCurrency = Math.abs(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        currency: parseOptions.currency || "USD",
        style: "currency",
      });

      return value < 0 ? `-${formattedCurrency}` : formattedCurrency;
    }
    return value;
  }

  if (typeof value === "string") {
    const date = formatDateIfValid(value, dateFormat);
    if (date) {
      return date;
    }
  }

  return value;
};

export function sanitizeFilename(filename) {
  if (!filename || typeof filename !== "string") {
    return "";
  }

  const basename = filename.split(/[\\/]/).pop();
  const lastDotIndex = basename.lastIndexOf(".");
  let nameOnly = lastDotIndex > 0 ? basename.slice(0, lastDotIndex) : basename;

  while (nameOnly.startsWith(".") || nameOnly.startsWith("-")) {
    // remove leading dots or dashes
    nameOnly = nameOnly.slice(1);
  }

  while (nameOnly.endsWith(".") || nameOnly.endsWith("-")) {
    // remove trailing dots or dashes
    nameOnly = nameOnly.slice(0, -1);
  }

  const sanitized = nameOnly
    .replace(/\s+/g, "_") // replace spaces with underscores
    .replace(/[^a-zA-Z0-9._-]/g, ""); // remove invalid characters(special characters)

  return sanitized;
}

export const safeJsonParse = (value, defaultValue) => {
  if (!value) return defaultValue;
  try {
    return JSON.parse(value);
  } catch {
    return defaultValue;
  }
};
